import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, CurrencyBtc, MapPin, Truck } from "@phosphor-icons/react";

const EMPTY = { name: "", phone: "", email: "", county: "los_angeles", pickup_address: "",
                dropoff_address: "", item_description: "", preferred_time: "" };

export default function DeliveryRequest() {
  const [counties, setCounties] = useState({});
  const [form, setForm] = useState(EMPTY);
  const [payment, setPayment] = useState("card");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/delivery/pricing").then(({ data }) => setCounties(data.counties)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const price = counties[form.county]?.price;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/delivery/request", {
        ...form, payment_method: payment, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create delivery request");
      setLoading(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="delivery-page">
      <p className="overline text-signal mb-3">// Local delivery · SoCal</p>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-[0.95] mb-6">
        Request a delivery.<br /><span className="neon-text">Prepay in seconds.</span>
      </h1>
      <p className="text-neutral-400 max-w-2xl leading-relaxed mb-12">
        We deliver across Los Angeles, Orange, Riverside, Ventura and San Bernardino counties.
        Tell us where to pick up and drop off, pay up front by card or crypto, and we handle the rest.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-14" data-testid="county-pricing">
        {Object.entries(counties).map(([k, c]) => (
          <button key={k} type="button" onClick={() => setForm({ ...form, county: k })}
            className={`text-left p-5 border transition-colors ${form.county === k
              ? "border-signal bg-[#111111] shadow-[0_0_18px_rgba(255,7,58,0.45)]" : "border-white/10 bg-[#111111] hover:border-signal"}`}
            data-testid={`county-${k}`}>
            <MapPin size={20} weight="duotone" className={form.county === k ? "text-signal" : ""} />
            <p className="font-display font-bold mt-3">{c.label}</p>
            <p className="font-mono text-lg mt-1">${c.price.toFixed(2)}</p>
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-5 gap-10" data-testid="delivery-form">
        <div className="lg:col-span-3 space-y-8">
          <section>
            <p className="overline mb-4">01 / Contact</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <input className="input-sharp" placeholder="Full name" required value={form.name} onChange={set("name")} data-testid="delivery-name" />
              <input className="input-sharp" placeholder="Phone" required value={form.phone} onChange={set("phone")} data-testid="delivery-phone" />
              <input className="input-sharp" type="email" placeholder="Email" required value={form.email} onChange={set("email")} data-testid="delivery-email" />
            </div>
          </section>
          <section>
            <p className="overline mb-4">02 / Route</p>
            <div className="space-y-3">
              <input className="input-sharp" placeholder="Pickup address" required value={form.pickup_address} onChange={set("pickup_address")} data-testid="delivery-pickup" />
              <input className="input-sharp" placeholder="Drop-off address" required value={form.dropoff_address} onChange={set("dropoff_address")} data-testid="delivery-dropoff" />
              <textarea className="input-sharp" rows="3" placeholder="What are we delivering? (size, weight, notes)" required
                        value={form.item_description} onChange={set("item_description")} data-testid="delivery-items" />
              <input className="input-sharp" placeholder="Preferred date / time (optional)" value={form.preferred_time} onChange={set("preferred_time")} data-testid="delivery-time" />
            </div>
          </section>
          <section>
            <p className="overline mb-4">03 / Payment</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[{ v: "card", icon: <CreditCard size={20} />, label: "Card" },
                { v: "crypto", icon: <CurrencyBtc size={20} />, label: "Crypto" }].map((o) => (
                <button key={o.v} type="button" onClick={() => setPayment(o.v)}
                  className={`text-left p-5 border transition-colors ${payment === o.v
                    ? "border-signal bg-[#111111]" : "border-white/10 bg-[#111111] hover:border-signal"}`}
                  data-testid={`delivery-pay-${o.v}`}>
                  {o.icon}<p className="font-display font-bold mt-2">{o.label}</p>
                </button>
              ))}
            </div>
          </section>
        </div>

        <aside className="lg:col-span-2 border border-white/10 bg-[#111111] p-6 h-fit lg:sticky lg:top-24" data-testid="delivery-summary">
          <Truck size={28} weight="duotone" className="text-signal mb-4" />
          <p className="overline opacity-60 mb-2">Delivery fee</p>
          <p className="font-display font-black text-5xl tracking-tighter mb-1" data-testid="delivery-price">
            {price != null ? `$${price.toFixed(2)}` : "—"}
          </p>
          <p className="font-mono text-xs opacity-60 mb-8">{counties[form.county]?.label} · flat rate</p>
          <button type="submit" disabled={loading} className="btn-signal w-full justify-center" data-testid="delivery-submit">
            {loading ? "Redirecting…" : "Prepay & request →"}
          </button>
          <p className="font-mono text-[10px] opacity-50 mt-4 leading-relaxed">
            Secure checkout via Stripe. We'll confirm your pickup window by phone or email.
          </p>
        </aside>
      </form>
    </main>
  );
}
